import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { AchooTabsComponent } from '../../core/achoo-tabs/achoo-tabs.component';
import { WebUtils } from '../../utils/web.utils';
import { Router, ActivatedRoute } from '@angular/router';
import {Location as NgCommonLocation} from '@angular/common';
import { UserDetailsComponent } from './user-settings/user-details/user-details.component';
import { UserService } from '../../services/user.service';
import {AbstractAchooComponent} from '../abstract-achoo.component';
import {UserBilling, UserMonthlyUsage} from '../../../models/Billing/billing-details';
import {UserBillingComponent} from '../user/user-billing/user-billing.component';
import {TranslateService} from '@ngx-translate/core';
import {AchooPopupComponent} from '../../core/achoo-popup/achoo-popup.component';
import {ModalService} from '../../services/modal/modal.service';
import {AppSettings} from '../../utils/app-settings';
import { Subscription } from 'rxjs/Subscription';
import { CompanyStatus } from '../../../models/common';
import { tap, catchError, concatMap } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';

enum AccountSettingTabs {
  USER_SETTINGS = 'user-settings',
  BILLING = 'billing'
}

@Component({
  selector: 'account-setting',
  templateUrl: './account-setting.component.html',
  styleUrls: ['./account-setting.component.scss']
})
export class AccountSettingComponent extends AbstractAchooComponent implements OnInit, OnDestroy {
  @ViewChild('accountSettingTabs') accountSettingTabs: AchooTabsComponent;
  @ViewChild('userBillingDetails') userBillingDetails: UserBillingComponent;
  @ViewChild('ErrorNotification') errorNotification: AchooPopupComponent;

  public tabKey: string;
  public accountTabs = AccountSettingTabs;
  public userBilling: UserBilling = new UserBilling();
  public isAccountInactive: boolean;
  public shouldReloadPage: boolean;
  public errorMessage: string;
  public userMonthlyUsage: UserMonthlyUsage = { isLoading: true };

  private _selfLoadingBillingSubscription: Subscription;

  constructor(private router: Router,
              private translate: TranslateService,
              private location: NgCommonLocation,
              private activatedRoute: ActivatedRoute,
              private modalService: ModalService,
              public userService: UserService) {
    super(userService);
    this.allowInactiveAccount = true;
  }

  ngOnInit() {
    super.ngOnInit();
    this.activatedRoute.data.subscribe(data => {
      this.tabKey = data.activeTab || AccountSettingTabs.USER_SETTINGS;
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.cancelCurrentLoadBilling();
  }

  companyChangeCallback(companyId) {
    this.clearThePricingOnChangeCompany();
    this.loadBilling(true);
  }

  private loadBilling(forInitPage = false): void {
    this.cancelCurrentLoadBilling();

    let oldBillingCustomerStatus = null;

    if (!forInitPage) {
      oldBillingCustomerStatus = this.userBilling.customerStatus;
    }

    this.userBilling.isLoading = true;
    this.userMonthlyUsage = { isLoading: true };
    this.shouldReloadPage = false;
    this._selfLoadingBillingSubscription = this.userService.getUserBilling(this.companyId).pipe(
      tap((res) => {
        this.userBilling = Object.assign({}, res, {
          isLoading: false
        });
        this.isAccountInactive = this.isBillingCustomerStatusInactive(this.userBilling.customerStatus);

        // loading page and change current account case
        if (forInitPage) {
          // for the case BE has not synced customer status with latest subscription status
          // or FE loaded customer status is old
          this.shouldReloadPage = this.isAccountInactive !== this.isCurrentAdvertiserInactive();
          return;
        }

        // after close modal case
        this.shouldReloadPage = this.shouldReloadPageByCustomerStatusChange(oldBillingCustomerStatus, res.customerStatus);
      }),
      catchError((err) => {
        this.userBilling.isLoading = false;
        this.modalService.close();
        this.errorMessage = this.translate.instant('user_billing.load_subscription_error');
        this.modalService.open(this.errorNotification, null, true);
        return of(null);
      })
    ).pipe(
      concatMap(() => {
        return this.userService.getUserMonthlyUsage(this.companyId).pipe(
          tap(res => {
            if (res && res.data) {
              this.userMonthlyUsage = { ...res.data, isLoading: false };
            }
          }),
          catchError(error => {
            this.userMonthlyUsage = { isLoadingError: true };
            this.modalService.close();
            this.errorMessage = this.translate.instant('user_billing.load_monthly_usage_error');
            this.modalService.open(this.errorNotification, null, true);
            WebUtils.reportErrorToSentry('Loading user monthly usage fails.', error);
            return of(null);
          })
        );
      })
    ).subscribe();
  }

  private clearThePricingOnChangeCompany() {
    if (this.userBillingDetails && this.userBillingDetails.userBillingDetails) {
      this.userBillingDetails.userBillingDetails.invoicedNextMonth = null;
    }
  }

  replaceTab(tabKey: string) {
    if (!tabKey) {
      this.tabKey = AccountSettingTabs.USER_SETTINGS;
    } else {
      this.tabKey = tabKey;
    }

    const params = {
      queryParams: {
        activeAccount: this.userService.getCompanyId()
      }
    };

    const url = this.router.createUrlTree(['/account-settings', tabKey], params);
    this.location.replaceState(url.toString());
  }

  get header() {
    if (this.tabKey === AccountSettingTabs.BILLING) {
      return this.translate.instant('account_settings.billing_header');
    }

    return this.translate.instant('account_settings.user_setting_header');
  }

  get subheader() {
    if (this.tabKey === AccountSettingTabs.BILLING) {
      return this.translate.instant('account_settings.billing_sub_header');
    }

    return this.translate.instant('account_settings.user_setting_sub_header');
  }

  public reloadBilling() {
    this.loadBilling();
  }

  private cancelCurrentLoadBilling() {
    if (this._selfLoadingBillingSubscription) {
      this._selfLoadingBillingSubscription.unsubscribe();
    }
  }

  private shouldReloadPageByCustomerStatusChange(oldStatus: CompanyStatus, currentStatus: CompanyStatus): boolean {
    // just show message for case inactive -> active
    const isOldStatusInactive = this.isBillingCustomerStatusInactive(oldStatus);

    if (!isOldStatusInactive) {
      return false;
    }

    return !this.isBillingCustomerStatusInactive(currentStatus);
  }

  private isBillingCustomerStatusInactive(customerStatus: CompanyStatus): boolean {
    return customerStatus === CompanyStatus.BLOCKED
      || customerStatus === CompanyStatus.CHURNED
      || customerStatus === CompanyStatus.FUTURE;
  }

  private isCurrentAdvertiserInactive(): boolean {
    const ad = this.userService.getCurrentAdvertiser();
    return ad && ad.inactive;
  }
}



// WEBPACK FOOTER //
// ./src/app/main/account-setting/account-setting.component.ts